/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FirebaseRemoteConfig } from "@capacitor-firebase/remote-config";
import { AxiosError } from "axios";
import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

import store, { AppDispatch, useAppDispatch } from "../../../store/rootStore";
import { log } from "../../../util/log";
import apiClient from "../../api-client";
import { farmlandsProApi } from "../../farmlandspro-api";
import withFirebaseSafety from "../utils/withFirebaseSafety";

import RemoteConfigSlice, {
  remoteConfigInitialState,
  selectRemoteConfig,
} from "./remoteConfig.slice";
import { RemoteConfigStateMachine } from "./RemoteConfigStateMachine";
import { syncRemoteConfigKey } from "./syncRemoteConfigKey";
import { ErrorCode, ServiceUnavailableResponse } from "./types";

const syncConfigFromFirebase = async (
  dispatch: AppDispatch,
  minimumFetchIntervalInSeconds: number
) => {
  await FirebaseRemoteConfig.setSettings({ minimumFetchIntervalInSeconds });
  await FirebaseRemoteConfig.fetchAndActivate().catch((e) => log(e));
  const currentConfig = selectRemoteConfig(store.getState());
  await Promise.all(
    Object.keys(remoteConfigInitialState).map((key) =>
      syncRemoteConfigKey(key, dispatch, currentConfig)
    )
  );
};

const getFetchIntervalSeconds = () => {
  const currentConfig = selectRemoteConfig(store.getState());
  return currentConfig.maintenance ? 60 : currentConfig.interval;
};

const RemoteConfigComponent: React.FC<{ children: React.ReactNode | React.ReactNode[] }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const { maintenance, interval } = useSelector(selectRemoteConfig);
  const fetchIntervalSeconds = maintenance ? 60 : interval;

  const maintenanceErrorInterceptor = useCallback(
    async (error: AxiosError<ServiceUnavailableResponse, unknown>) => {
      if (
        error.code === "ERR_BAD_RESPONSE" &&
        error.response?.status === 503 &&
        error.response?.data.type === ErrorCode.ServiceUnavailable
      ) {
        await syncConfigFromFirebase(dispatch, 0);
        dispatch(
          RemoteConfigSlice.actions.updateKey({
            key: "maintenance",
            value: true,
          })
        );
        await syncConfigFromFirebase(dispatch, getFetchIntervalSeconds());
      }

      throw error;
    },
    [dispatch]
  );

  useEffect(() => {
    const axiosInstances = [apiClient.axiosInstance, farmlandsProApi.client];
    const interceptorIds = axiosInstances.map((instance) =>
      instance.interceptors.response.use(null, maintenanceErrorInterceptor)
    );

    return () => {
      axiosInstances.forEach((instance, index) => {
        instance.interceptors.response.eject(interceptorIds[index]);
      });
    };
  }, [maintenanceErrorInterceptor]);

  useEffect(() => {
    let pollInterval: ReturnType<typeof setInterval> | undefined;
    let cancelled = false;

    const setup = async () => {
      try {
        await syncConfigFromFirebase(dispatch, fetchIntervalSeconds);
      } catch (e) {
        log(e);
      }

      if (cancelled) {
        return;
      }

      pollInterval = setInterval(() => {
        syncConfigFromFirebase(dispatch, fetchIntervalSeconds).catch((e) => log(e));
      }, fetchIntervalSeconds * 1000);
    };

    setup().catch((e) => log(e));

    return () => {
      cancelled = true;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [dispatch, fetchIntervalSeconds]);

  return <RemoteConfigStateMachine>{children}</RemoteConfigStateMachine>;
};

export const RemoteConfigProvider = withFirebaseSafety(RemoteConfigComponent);
